// src/components/ui/Input.jsx
//
// Primitives layer, phase 1. Formalizes the glass-card input pattern
// already used identically 8 separate times across LoginPage.jsx/
// RegisterPage.jsx (icon + rounded-2xl translucent field + red-border
// error state) -- and adds the light-mode variant none of those 8
// instances ever had, since the auth pages themselves were dark-only.
import React, { forwardRef, useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { useTranslation } from 'react-i18next';

/**
 * @param {React.ComponentType} icon - a lucide-react icon component, rendered on the left
 * @param {string} error - error message; when set, renders below the field and reddens the border
 * @param {boolean} isPassword - adds a show/hide toggle instead of needing a manual type-swap
 */
const Input = forwardRef(function Input(
  { icon: Icon, error, isPassword = false, type, className = '', ...rest },
  ref
) {
  const { t } = useTranslation();
  const [showPassword, setShowPassword] = useState(false);
  const resolvedType = isPassword ? (showPassword ? 'text' : 'password') : type;

  return (
    <div>
      <div className="relative">
        {Icon && (
          <Icon className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500 w-5 h-5 pointer-events-none" />
        )}
        <input
          ref={ref}
          type={resolvedType}
          className={`w-full py-4 rounded-2xl outline-none transition-colors border
            bg-white dark:bg-white/5
            text-gray-900 dark:text-white
            placeholder-gray-400 dark:placeholder-gray-500
            ${Icon ? 'pl-12' : 'pl-4'} ${isPassword ? 'pr-12' : 'pr-4'}
            ${
              error
                ? 'border-red-500'
                : 'border-gray-300 dark:border-white/10 hover:border-gray-400 dark:hover:border-white/20 focus:border-violet-500 dark:focus:border-violet-400'
            } ${className}`}
          {...rest}
        />
        {isPassword && (
          <button
            type="button"
            onClick={() => setShowPassword((s) => !s)}
            aria-label={showPassword ? t('common.hidePassword') : t('common.showPassword')}
            className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
          >
            {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
          </button>
        )}
      </div>
      {typeof error === 'string' && error.trim() && (
        <p className="text-red-600 dark:text-red-400 text-sm mt-1">{error}</p>
      )}
    </div>
  );
});

export default Input;
